import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function changeLanguage(code: string) {
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    setOpen(false);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-full border border-background-300/70 bg-background-50 px-3 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
        aria-label="Change language"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span>{current.label}</span>
        <i className="ri-arrow-down-s-line text-foreground-500 w-4 h-4 flex items-center justify-center" />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-lg border border-background-300/70 bg-background-50 py-1">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => changeLanguage(lang.code)}
              className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap ${
                lang.code === current.code
                  ? 'font-semibold text-primary-700'
                  : 'text-foreground-700'
              }`}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span>{lang.label}</span>
              {lang.code === current.code && (
                <i className="ri-check-line ml-auto text-primary-600 w-4 h-4 flex items-center justify-center" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}